/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IndexTable,
  LegacyCard,
  useIndexResourceState,
  Badge,
  Button,
  Page,
  SkeletonDisplayText,
  InlineStack,
  BlockStack,
  Banner,
  List,
  Text,
  Modal,
} from "@shopify/polaris";
import { EditIcon, DeleteIcon, RefreshIcon, PinIcon, AlertTriangleIcon } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { useDeleteExportRuleMutation, useGetExportRulesQuery } from "../store/store/slices/ExportRTK";
import { useState } from "react";
import dayjs from "dayjs";

export default function ExportRulesTable() {
  const navigate = useNavigate();
  const [delRuleId, setDelRuleId] = useState("");
  const [open, setOpen] = useState(false);
  const theme = localStorage.getItem("theme") || "light";
  const [deleteExportRule, { isLoading: deletingRule }] = useDeleteExportRuleMutation()
  // ✅ Fetch rules from backend
  const { data, isLoading, refetch, isFetching } = useGetExportRulesQuery();

  const rules = data?.data || [];

  const { handleSelectionChange } = useIndexResourceState(rules);

  const deleteExportRuleHandler = async () => {
    setOpen(false);
    await deleteExportRule(delRuleId).unwrap();
  }
  const openConfirm = (id: string) => {
    setDelRuleId(id);
    setOpen(true);
  }
  const closeModal = () => {
    setOpen(false)
  }
  const rowMarkup =
    !isLoading &&
    rules.map((rule: any, index: number) => (
      <IndexTable.Row
        id={rule._id}
        key={rule._id}
        selected={false}
        position={index}
        rowType="data"
      >
        <IndexTable.Cell>{rule.name}</IndexTable.Cell>
        {/* <IndexTable.Cell>
          <InlineStack gap={"100"}>
            {rule.columns.map((col: string) => (
              <Badge key={col} tone="info">
                {col}
              </Badge>
            ))}
          </InlineStack>
        </IndexTable.Cell> */}

        <IndexTable.Cell>
          {rule.exportRangeDays}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap={"100"}>

            <Button
              icon={EditIcon}
              onClick={() => navigate(`/dashboard/export-rules/${rule._id}/edit`)}
            />
            <Button
              icon={DeleteIcon}
              tone="critical"
              disabled={deletingRule && rule._id === delRuleId}
              loading={deletingRule && rule._id === delRuleId}
              onClick={() => openConfirm(rule._id)}
            />
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{dayjs(rule.createdAt).format("hh:mm A, DD MMM, YYYY")}</IndexTable.Cell>
      </IndexTable.Row>
    ));

  return (
    <Page
      title="Export Rules"
      titleMetadata={<Badge tone="success-strong">Csv format</Badge>}
      additionalMetadata={<Badge tone="new" size="large" icon={AlertTriangleIcon}>These rules will only be applied for staff member export</Badge>}
      backAction={{
        onAction: () => {
          navigate(-1)
        }
      }}

      primaryAction={
        <Button
          variant={theme === "dark" ? "secondary" : "primary"}
          onClick={() => navigate("/dashboard/export-rules/create")}
        >
          Create Export Rule
        </Button>
      }
      secondaryActions={
        <Button icon={RefreshIcon} loading={isFetching} onClick={() => {
          refetch()
        }}></Button>
      }
    >
      <LegacyCard>
        <IndexTable
          hasZebraStriping
          selectable={false}
          resourceName={{ singular: "rule", plural: "rules" }}
          emptyState={isLoading ? <Text as="h3">Fetching rules...</Text> : <BlockStack>
            <Banner
              tone="info"
              icon={PinIcon}
              title="No export rules created yet"
            >
              <p>
                You haven’t created any export rules yet. Export rules allow you to control
                which order columns can be exported, which staff members are allowed to
                generate exports, and who receives email alerts when an export is triggered.
              </p>
              <List>
                <List.Item>Select which order data columns can be exported</List.Item>
                <List.Item>Assign staff members who are allowed to export data</List.Item>
                <List.Item>Choose users who will receive export alert emails</List.Item>
                <List.Item>Enable admin notifications</List.Item>
              </List>

              <div style={{ marginTop: "12px" }}>
                <Button
                  variant="primary"
                  onClick={() => navigate("/dashboard/export-rules/create")}
                >
                  Create Export Rule
                </Button>
              </div>
            </Banner>
          </BlockStack>}
          itemCount={rules.length}
          onSelectionChange={handleSelectionChange}
          loading={isLoading || isFetching}
          headings={[
            { title: "Rule Name", tooltipContent: "Name of the rule" },
            // { title: "Columns", tooltipContent: "Only these columns of orders data will be exported as csv file" },
            {
              title: "Export Date Limit",
              tooltipContent: "Staff can export data only from the last X days."
            },
            { title: "Actions" },
            { title: "Created" },
          ]}
        >
          {rowMarkup}
        </IndexTable>
      </LegacyCard>

      {isLoading && (
        <div style={{ width: "300px", margin: "2rem auto" }}>
          <SkeletonDisplayText size="large" maxWidth="100%" />
        </div>
      )}
      <Modal open={open} title="Delete rule confirmation" primaryAction={{ content: "Yes, Delete", destructive: true, onAction: () => deleteExportRuleHandler() }} secondaryActions={[{ content: "No, Close", onAction: () => closeModal() }]} onClose={closeModal}>
        <Modal.Section subdued>
          <BlockStack gap={"300"}>
            <Text as="p">This action can not be undone.</Text>
            <div>
              <Badge tone="attention">Are sure to delete this export rule?</Badge>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}